import React from 'react';

/**
 * Error Boundary component for catching and displaying React errors
 * 
 * Use this to wrap major sections of the UI to prevent the entire
 * application from crashing when a component throws an error.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log error to console
    console.error('Error caught by boundary:', error, errorInfo);
    
    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          errorInfo: this.state.errorInfo,
          retry: this.handleRetry
        });
      }

      // Default error UI
      return (
        <div className={`error-boundary ${this.props.className || ''}`} 
             style={{
               padding: '20px',
               backgroundColor: '#fef2f2',
               border: '1px solid #fecaca',
               borderRadius: '8px',
               margin: '10px',
               ...this.props.style
             }}>
          <h3 style={{ color: '#991b1b', marginBottom: '10px' }}>
            {this.props.title || 'Something went wrong'}
          </h3>
          <p style={{ color: '#7f1d1d', marginBottom: '15px' }}>
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={this.handleRetry}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
          {this.props.showDetails && this.state.errorInfo && (
            <details style={{ marginTop: '15px' }}>
              <summary style={{ cursor: 'pointer', color: '#7f1d1d' }}>
                Error Details
              </summary>
              <pre style={{ 
                marginTop: '10px', 
                padding: '10px', 
                backgroundColor: '#fee2e2',
                borderRadius: '4px',
                overflow: 'auto',
                fontSize: '12px'
              }}>
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Panel-specific error boundary with minimal styling
 */
export function PanelErrorBoundary({ children, panelName }) {
  return (
    <ErrorBoundary 
      title={`Error in ${panelName}`}
      fallback={({ error, retry }) => (
        <div className="flex flex-col items-center justify-center p-4 bg-red-50 border border-red-200 rounded m-2">
          <span className="text-red-600 text-sm mb-2">
            {panelName} encountered an error
          </span>
          <button 
            onClick={retry}
            className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
          >
            Retry
          </button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Visualizer-specific error boundary
 */
export function VisualizerErrorBoundary({ children }) {
  return (
    <ErrorBoundary
      title="3D Visualizer Error"
      fallback={({ error, retry }) => (
        <div className="flex flex-col items-center justify-center h-full bg-gray-900 text-gray-300">
          <svg 
            className="w-16 h-16 mb-4 text-red-500" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
            />
          </svg>
          <p className="text-lg mb-2">3D Visualizer Error</p>
          <p className="text-sm text-gray-400 mb-4">{error?.message}</p>
          <button
            onClick={retry}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Reload Visualizer
          </button>
        </div>
      )}
      showDetails
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Modal-specific error boundary
 */
export function ModalErrorBoundary({ children, modalName }) {
  return (
    <ErrorBoundary
      title={`Error in ${modalName || 'Modal'}`}
      fallback={({ error, retry }) => (
        <div className="p-6 text-center">
          <p className="text-red-600 mb-4">
            An error occurred while displaying this dialog.
          </p>
          <p className="text-sm text-gray-500 mb-4">{error?.message}</p>
          <button
            onClick={retry}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Try Again
          </button>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Hook for error boundary integration with functional components
 * Use with the ErrorBoundary component
 */
export function useErrorHandler() {
  const [error, setError] = React.useState(null);
  
  const resetError = React.useCallback(() => {
    setError(null);
  }, []);
  
  const handleError = React.useCallback((err) => {
    setError(err);
    console.error('Error handled:', err);
  }, []);
  
  // Throw error to be caught by nearest ErrorBoundary
  if (error) {
    throw error;
  }
  
  return { handleError, resetError };
}

export default ErrorBoundary;
